import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';
import { OwlLearning } from '../components/OwlMascot';
import { Clock, BookOpen, Users, ArrowRight, ChevronRight, ArrowLeft, Lock, Sparkles, Zap, Crown, Play, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_CONFIG = {
  prog_2h: { plan: 'decouverte', label_fr: 'IA Starter · Gratuit', label_en: 'IA Starter · Free', color: 'bg-gray-100 text-brand-dark border-gray-200', icon: Sparkles },
  prog_4h: { plan: 'essentiel', label_fr: 'IA Fondations · 19€/mois', label_en: 'IA Fondations · €19/mo', color: 'bg-blue-50 text-brand-blue border-blue-200', icon: Zap },
  prog_8h: { plan: 'pro', label_fr: 'IA Pro · 49€/mois', label_en: 'IA Pro · €49/mo', color: 'bg-orange-50 text-brand-orange border-orange-200', icon: Crown },
  prog_2x2h: { plan: 'entreprise', label_fr: 'IA Expert · 99€/pers/mois', label_en: 'IA Expert · €99/person/mo', color: 'bg-orange-50 text-brand-orange border-orange-200', icon: Building2 },
};

const PLAN_ORDER = ['none', 'decouverte', 'essentiel', 'pro', 'entreprise'];

export default function ProgramsPage() {
  const { programId } = useParams();
  const { language, t } = useLanguage();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [modules, setModules] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  const userPlan = user?.plan || 'none';

  const hasAccess = (programId) => {
    const config = PLAN_CONFIG[programId];
    if (!config) return true;
    return PLAN_ORDER.indexOf(userPlan) >= PLAN_ORDER.indexOf(config.plan);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (programId) {
          const [progRes, modsRes] = await Promise.all([
            axios.get(`${API}/programs/${programId}`),
            axios.get(`${API}/programs/${programId}/modules`)
          ]);
          setSelectedProgram(progRes.data);
          setModules(modsRes.data);
          if (user) {
            const progressRes = await axios.get(`${API}/progress`, { headers: getAuthHeaders(), withCredentials: true });
            setUserProgress(progressRes.data.progress || []);
          }
        } else {
          setSelectedProgram(null);
          setModules([]);
          const res = await axios.get(`${API}/programs`);
          setPrograms(res.data);
        }
      } catch (err) {
        console.error('Programs fetch error:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [programId, user, getAuthHeaders]);

  const levelColors = {
    beginner: 'bg-blue-50 text-brand-blue border-blue-200',
    intermediate: 'bg-orange-50 text-brand-orange border-orange-200',
    advanced: 'bg-gray-100 text-brand-dark border-gray-300'
  };
  const levelMap = { beginner: t('beginner'), intermediate: t('intermediate'), advanced: t('advanced') };

  const isModuleComplete = (modId) => userProgress.some(p => p.module_id === modId && p.completed);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Program detail view
  if (selectedProgram) {
    const completedCount = modules.filter(m => isModuleComplete(m.module_id)).length;
    const pct = modules.length > 0 ? Math.round((completedCount / modules.length) * 100) : 0;

    return (
      <div className="min-h-screen bg-[#E8ECF1]" data-testid="program-detail-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button variant="ghost" className="mb-6 text-slate-600" onClick={() => navigate('/programs')} data-testid="back-to-programs">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>

          {/* Program Header */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
            <div className="relative h-48 overflow-hidden">
              <img src={selectedProgram.image} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute top-4 left-6">
                <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg shadow-lg ring-2 ring-white/30" data-testid="program-detail-logo" />
              </div>
              <div className="absolute bottom-4 left-6 right-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="text-white border-0" style={{ backgroundColor: selectedProgram.color }}>
                    {selectedProgram.duration}
                  </Badge>
                  <Badge className={levelColors[selectedProgram.level]}>
                    {levelMap[selectedProgram.level]}
                  </Badge>
                </div>
                {!user ? (
                  <Button
                    size="sm"
                    className="bg-brand-orange hover:bg-orange-600 text-white font-semibold shadow-lg gap-1.5"
                    style={{ fontFamily: 'Poppins' }}
                    onClick={(e) => { e.stopPropagation(); navigate('/auth?mode=register'); }}
                    data-testid="program-detail-register-cta"
                  >
                    {language === 'fr' ? "S'inscrire" : 'Sign up'} <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : !hasAccess(selectedProgram.program_id) ? (
                  <Button
                    size="sm"
                    className="bg-brand-blue hover:bg-blue-600 text-white font-semibold shadow-lg gap-1.5"
                    style={{ fontFamily: 'Poppins' }}
                    onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}
                    data-testid="program-detail-upgrade-cta"
                  >
                    {language === 'fr' ? 'Voir les plans' : 'View plans'} <ArrowRight className="w-4 h-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="p-6">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="program-title">
                {language === 'fr' ? selectedProgram.title_fr : selectedProgram.title_en}
              </h1>
              <p className="text-slate-500 mt-2">
                {language === 'fr' ? selectedProgram.description_fr : selectedProgram.description_en}
              </p>
              <div className="flex items-center gap-4 mt-4 text-sm text-slate-400">
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {selectedProgram.duration}</span>
                <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> {modules.length} {t('modules')}</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {language === 'fr' ? selectedProgram.target_fr : selectedProgram.target_en}</span>
              </div>
              {user && (
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{completedCount}/{modules.length} {t('modules')}</span>
                    <span className="text-blue-600 font-medium">{pct}%</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
              )}
            </div>
          </div>

          {/* Modules List */}
          <h2 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>
            {t('modules')}
          </h2>
          <div className="space-y-3">
            {modules.map((mod, i) => {
              const done = isModuleComplete(mod.module_id);
              return (
                <div
                  key={mod.module_id}
                  className={`bg-white rounded-xl border p-5 cursor-pointer group hover:shadow-sm transition-shadow ${
                    done ? 'border-brand-blue/30 bg-blue-50/30' : 'border-slate-200'
                  }`}
                  onClick={() => {
                    if (user) {
                      navigate(`/course/${mod.module_id}`);
                    } else {
                      navigate('/auth?mode=register');
                    }
                  }}
                  data-testid={`module-item-${mod.module_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                          done ? 'bg-blue-100 text-brand-blue' : 'bg-blue-50 text-blue-600'
                        }`} style={{ fontFamily: 'Poppins' }}>
                          {done ? '✓' : i + 1}
                        </div>
                        <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="" className="absolute -bottom-1 -right-1 w-4 h-4 rounded shadow-sm ring-1 ring-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">
                          {language === 'fr' ? mod.title_fr : mod.title_en}
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {language === 'fr' ? mod.description_fr : mod.description_en}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{mod.duration_minutes} min</span>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 transition-colors" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Programs list view
  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="programs-list-page">
      <SEO
        title={language === 'fr' ? 'Programmes de Formation IA' : 'AI Training Programs'}
        description={language === 'fr'
          ? "Découvrez nos 4 programmes de formation IA : Initiation Express, Fondamentaux, Formation Complète et IA pour les Pros. Du débutant à l'expert."
          : "Discover our 4 AI training programs: Express Introduction, Fundamentals, Complete Training and AI for Professionals. From beginner to expert."}
        path="/programs"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Programmes de Formation IA",
          "description": "4 programmes de formation IA, du débutant à l'expert",
          "numberOfItems": 4,
          "itemListElement": programs.map((p, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Course",
              "name": language === 'fr' ? p.title_fr : p.title_en,
              "description": language === 'fr' ? p.description_fr : p.description_en,
              "provider": { "@type": "Organization", "name": "IA Academy", "url": "https://ia-academy.ai" }
            }
          }))
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" data-testid="programs-logo" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-none" style={{ fontFamily: 'Poppins' }} data-testid="programs-title">
                {t('programs_title')}
              </h1>
              <p className="text-xs text-slate-400 tracking-wider font-medium mt-0.5">
                {language === 'fr' ? 'IA Academy — Progressez. 30 min/jour. Résultats concrets.' : 'IA Academy — Level Up. 30 Min a Day. Real Results.'}
              </p>
            </div>
          </div>
          <p className="text-slate-500 mt-2 text-base md:text-lg">{t('programs_subtitle')}</p>
          <div className="mt-3 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <Clock className="w-4 h-4 text-brand-blue" />
            <p className="text-sm text-brand-blue font-medium" data-testid="self-paced-message">
              {language === 'fr' 
                ? 'Apprenez à votre rythme — chaque module est accessible 24h/24. L\'abonnement payant est valable 1 mois (renouvelable).' 
                : 'Learn at your own pace — every module is available 24/7. Paid subscriptions are valid for 1 month (renewable).'}
            </p>
            <OwlLearning size={40} className="hidden sm:block shrink-0" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {programs.map((prog) => {
            const config = PLAN_CONFIG[prog.program_id] || {};
            const PlanIcon = config.icon || Sparkles;
            const locked = user && !hasAccess(prog.program_id);

            return (
              <div
                key={prog.program_id}
                className={`bg-white rounded-xl border overflow-hidden transition-all cursor-pointer group ${
                  locked ? 'border-slate-200 opacity-80' : 'border-slate-200 hover:shadow-lg'
                }`}
                onClick={() => {
                  if (!user) {
                    navigate('/auth?mode=register');
                  } else if (locked) {
                    navigate('/pricing');
                  } else {
                    navigate(`/programs/${prog.program_id}`);
                  }
                }}
                data-testid={`program-card-${prog.program_id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={prog.image}
                    alt=""
                    className={`w-full h-full object-cover transition-transform duration-500 ${locked ? 'grayscale-[30%]' : 'group-hover:scale-105'}`}
                  />
                  {locked && (
                    <div className="absolute inset-0 bg-slate-900/20 flex items-center justify-center">
                      <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2.5 flex items-center gap-2 shadow-lg">
                        <Lock className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-slate-800">
                          {language === 'fr' ? 'Plan requis' : 'Plan required'}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-7 h-7 rounded shadow ring-1 ring-white/30" />
                    <Badge className="text-white border-0 shadow-sm" style={{ backgroundColor: prog.color }}>
                      <Clock className="w-3 h-3 mr-1" /> {prog.duration}
                    </Badge>
                  </div>
                  <div className="absolute top-3 right-3">
                    <Badge className={levelColors[prog.level]}>
                      {levelMap[prog.level]}
                    </Badge>
                  </div>
                  {/* Plan badge at bottom of image */}
                  <div className="absolute bottom-3 left-3">
                    <Badge className={`border shadow-sm ${config.color || 'bg-slate-100 text-slate-600'}`} data-testid={`plan-badge-${prog.program_id}`}>
                      <PlanIcon className="w-3 h-3 mr-1" />
                      {language === 'fr' ? config.label_fr : config.label_en}
                    </Badge>
                  </div>
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Poppins' }}>
                    {language === 'fr' ? prog.title_fr : prog.title_en}
                  </h2>
                  <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                    {language === 'fr' ? prog.description_fr : prog.description_en}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {prog.modules_count} {t('modules')}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {language === 'fr' ? prog.target_fr : prog.target_en}</span>
                    </div>
                    {locked ? (
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 gap-1 p-0" data-testid={`upgrade-btn-${prog.program_id}`}>
                        {language === 'fr' ? 'Voir les plans' : 'View plans'} <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    ) : !user ? (
                      <Button variant="ghost" size="sm" className="text-brand-orange hover:text-orange-700 gap-1 p-0 font-semibold" data-testid={`register-btn-${prog.program_id}`}>
                        {language === 'fr' ? 'S\'inscrire' : 'Sign up'} <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-brand-blue hover:text-blue-700 gap-1 p-0">
                        {t('start_program')} <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
